import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { formatDate } from '@/utils/formatters';
// GenericDrawer is a JS file with many props that tsc cannot fully infer; cast to permissive FC
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import _GenericDrawer, { DRAWER_STYLES, DRAWER_COMPONENTS } from '@/components/ui/GenericDrawer';
const GenericDrawer = _GenericDrawer as React.FC<any>;
// FieldsetCard is a JS file whose inferred prop types mark optional params as required;
// cast to a permissive FC to avoid TS2740 until FieldsetCard.jsx is migrated
import _FieldsetCard from '@/components/ui/FieldsetCard';
const FieldsetCard = _FieldsetCard as React.FC<{
  title?: string;
  count?: number;
  actionIcon?: React.ReactNode;
  onAction?: () => void;
  actionTitle?: string;
  actionLabel?: string;
  rightContent?: React.ReactNode;
  shadow?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
}>;
import Avatar from '@/components/ui/Avatar';
// SearchableSelect is a JS component with restrictively-inferred props; cast until migrated
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import _SearchableSelect from '@/components/ui/SearchableSelect';
const SearchableSelect = _SearchableSelect as React.FC<any>;
// SmartSelect is the canonical pessoa picker (auto-detects client/server strategy)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import _SmartSelect from '@/components/ui/SmartSelect';
const SmartSelect = _SmartSelect as React.FC<any>;
import { Check, Clock, X, Users, GitBranch, Loader2 } from 'lucide-react';
import BrandSignatureSetup from './BrandSignatureSetup';

interface EmpresaProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

interface Cidade {
  id: string;
  cidade: string;
  cod?: string | null;
  estado_id?: string;
  estado?: { id: string; uf: string } | null;
}

interface Contrato {
  id: string;
  data_inicio: string;
  data_fim: string | null;
  status: string;
  limite_usuarios_custom: number | null;
  limite_filiais_custom: number | null;
  limite_storage_custom: number | null;
  plano: {
    id: string;
    nome: string;
    limite_usuarios: number;
    limite_filiais: number;
    limite_storage_gb: number;
  } | null;
}

interface TenantModulo {
  id: string;
  ativo: boolean;
  is_trial: boolean;
  trial_inicio: string | null;
  trial_fim: string | null;
  modulo: ModuloInfo | null;
}

interface ModuloInfo {
  id: string;
  modulo: string;
  descricao?: string | null;
  icone?: string | null;
  cor_bg?: string | null;
  cor_texto?: string | null;
  ordem?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormData = Record<string, any>;

/**
 * Valida CNPJ usando algoritmo dos dígitos verificadores
 */
function validateCNPJ(cnpj: string | null | undefined): boolean {
  if (!cnpj) return true; // Campo opcional

  // Remove formatação
  const cleaned = cnpj.replace(/\D/g, '');

  // Deve ter 14 dígitos
  if (cleaned.length !== 14) return false;

  // Verifica se todos os dígitos são iguais (CNPJs inválidos conhecidos)
  if (/^(\d)\1+$/.test(cleaned)) return false;

  // Cálculo do primeiro dígito verificador
  let sum = 0;
  let weight = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleaned[i]) * weight[i];
  }
  let remainder = sum % 11;
  let digit1 = remainder < 2 ? 0 : 11 - remainder;

  // Verifica primeiro dígito
  if (parseInt(cleaned[12]) !== digit1) return false;

  // Cálculo do segundo dígito verificador
  sum = 0;
  weight = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleaned[i]) * weight[i];
  }
  remainder = sum % 11;
  let digit2 = remainder < 2 ? 0 : 11 - remainder;

  // Verifica segundo dígito
  if (parseInt(cleaned[13]) !== digit2) return false;

  return true;
}

/**
 * Busca endereço via ViaCEP
 * @param {string} cep - CEP com ou sem formatação
 * @returns {Promise<Object|null>} Dados do endereço ou null se não encontrado
 */
async function fetchAddressByCEP(cep: string | null | undefined) {
  const cleaned = cep?.replace(/\D/g, '');
  if (!cleaned || cleaned.length !== 8) return null;

  try {
    const response = await fetch(`https://viacep.com.br/ws/${cleaned}/json/`);
    const data = await response.json();

    if (data.erro) return null;

    return {
      logradouro: data.logradouro || '',
      bairro: data.bairro || '',
      cidade: data.localidade || '',
      uf: data.uf || '',
      complemento: data.complemento || ''
    };
  } catch (error) {
    console.error('Erro ao buscar CEP:', error);
    return null;
  }
}

/**
 * Empresa - Drawer para visualização/edição dos dados da empresa do tenant
 * Acessível via UserMenu > "Minha Empresa"
 *
 * Seções:
 * - Dados da Empresa (editável): logo, nome, nome_fantasia, cnpj
 * - Endereço (editável): cep, logradouro, numero, complemento, bairro, localizacao, cidade
 * - Meu Plano (readonly): plano atual, limites, uso
 * - Módulos (readonly): lista de módulos com status
 *
 * PADRÃO: Usa GenericDrawer com renderContent para UI customizada.
 */
export default function Empresa({
  isOpen,
  onClose,
  onSave
}: EmpresaProps) {
  const { tenantId, hasPermission, getAuditFields } = useAuth();
  const { showSuccess, showError } = useToast();

  // Permissões
  const canEdit = hasPermission('EMPRESA', 'EDIT');

  // Controle de modo do drawer (view/edit)
  const [mode, setMode] = useState<'view' | 'edit' | 'create'>('view');

  // U18 — tab ativa do drawer (dados básicos vs. brand para assinatura).
  const [activeTab, setActiveTab] = useState<'dados' | 'brand'>('dados');

  // Reset mode + tab quando drawer fecha
  useEffect(() => {
    if (!isOpen) {
      setMode('view');
      setActiveTab('dados');
    }
  }, [isOpen]);

  // Estados para dados complementares (não gerenciados pelo GenericDrawer)
  const [contrato, setContrato] = useState<Contrato | null>(null);
  const [modulos, setModulos] = useState<TenantModulo[]>([]);
  const [allModulos, setAllModulos] = useState<ModuloInfo[]>([]);
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [usageMetrics, setUsageMetrics] = useState({ usuarios: 0, filiais: 0 });
  const [loadingCep, setLoadingCep] = useState(false);
  const [cnpjError, setCnpjError] = useState<string | null>(null);
  const [tenantConfig, setTenantConfig] = useState<Record<string, unknown>>({}); // Config JSONB do tenant

  // Formatador de CNPJ
  const formatCNPJ = useCallback((value: string | null | undefined): string => {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 14) {
      return cleaned
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
    return value.slice(0, 18);
  }, []);

  // Formatador de CEP
  const formatCEP = useCallback((value: string | null | undefined): string => {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 8) {
      return cleaned.replace(/(\d{5})(\d)/, '$1-$2');
    }
    return value.slice(0, 9);
  }, []);

  // Carregar cidades
  useEffect(() => {
    const loadCidades = async () => {
      try {
        const allCidades = [];
        let from = 0;
        const batchSize = 1000;
        let hasMore = true;

        while (hasMore) {
          const { data, error } = await supabase
            .from('cidade')
            .select('id, cidade, cod, estado_id, estado(id, uf)')
            .order('cidade')
            .range(from, from + batchSize - 1);

          if (error) throw error;

          if (data && data.length > 0) {
            allCidades.push(...data);
            from += batchSize;
            hasMore = data.length === batchSize;
          } else {
            hasMore = false;
          }
        }

        setCidades(allCidades);
      } catch (err) {
        console.error('Erro ao carregar cidades:', err);
      }
    };
    loadCidades();
  }, []);

  // Carregar todos os módulos disponíveis
  useEffect(() => {
    const loadAllModulos = async () => {
      try {
        const { data, error } = await supabase
          .from('modulo')
          .select('id, modulo, descricao, icone, cor_bg, cor_texto, ordem')
          .eq('ativo', true)
          .is('deleted_at', null)
          .order('ordem');

        if (error) throw error;
        setAllModulos(data || []);
      } catch (err) {
        console.error('Erro ao carregar módulos:', err);
      }
    };
    loadAllModulos();
  }, []);

  // Carregar logo do tenant
  const loadLogo = useCallback(async (tid: string | null) => {
    if (!tid) {
      setLogoUrl(null);
      return;
    }
    try {
      const { data } = await supabase
        .from('entidade_file')
        .select('file:file_id(nome_storage)')
        .eq('entidade', 'tenant')
        .eq('entidade_id', tid)
        .eq('papel', 'logo')
        .is('deleted_at', null)
        .maybeSingle();

      if (data?.file?.nome_storage) {
        const { data: urlData } = await supabase.storage
          .from('documento')
          .createSignedUrl(data.file.nome_storage, 60 * 60 * 24 * 365);
        setLogoUrl(urlData?.signedUrl || null);
      } else {
        setLogoUrl(null);
      }
    } catch (err) {
      setLogoUrl(null);
    }
  }, []);

  // Handler para carregar registro (usado pelo GenericDrawer)
  const handleLoadRecord = useCallback(async (id: string) => {
    // 1. Dados do tenant
    const { data: tenantRecord, error: tenantError } = await supabase
      .from('tenant')
      .select(`
        id, codigo, nome, nome_fantasia, cnpj, ativo, config, logo_url,
        cep, logradouro, numero, complemento, bairro, localizacao, cidade_id,
        cidade_rel:cidade_id(id, cidade, estado:estado_id(id, uf)),
        responsavel_obra_entrega_pessoa_id,
        responsavel_obra_entrega_rel:responsavel_obra_entrega_pessoa_id(id, nome, apelido)
      `)
      .eq('id', id)
      .single();

    if (tenantError) throw tenantError;

    // Formatar CNPJ e CEP, armazenar config e usa_bvs
    if (tenantRecord) {
      tenantRecord.cnpj = formatCNPJ(tenantRecord.cnpj);
      tenantRecord.cep = formatCEP(tenantRecord.cep);
      setTenantConfig((tenantRecord.config || {}) as Record<string, unknown>);
      // Passar usa_bvs para o formData
      (tenantRecord as FormData).usa_bvs = (tenantRecord.config as Record<string, unknown>)?.usa_bvs || false;
    }

    // 2. Contrato ativo
    const { data: contratoRecord, error: contratoError } = await supabase
      .from('tenant_contrato')
      .select(`
        id, data_inicio, data_fim, status,
        limite_usuarios_custom, limite_filiais_custom, limite_storage_custom,
        plano:plano_id(id, nome, limite_usuarios, limite_filiais, limite_storage_gb)
      `)
      .eq('tenant_id', id)
      .eq('status', 'ativo')
      .order('data_inicio', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (contratoError) throw contratoError;
    setContrato(contratoRecord as Contrato | null);

    // 3. Módulos contratados
    const { data: modulosRecord, error: modulosError } = await supabase
      .from('tenant_modulo')
      .select(`
        id, ativo, is_trial, trial_inicio, trial_fim,
        modulo:modulo_id(id, modulo, descricao, icone, cor_bg, cor_texto, ordem)
      `)
      .eq('tenant_id', id)
      .is('deleted_at', null);

    if (modulosError) throw modulosError;
    setModulos((modulosRecord || []) as TenantModulo[]);

    // 4. Métricas de uso
    const { count: usuariosCount } = await supabase
      .from('pessoa')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', id)
      .not('user_id', 'is', null)
      .is('deleted_at', null);

    const { count: filiaisCount } = await supabase
      .from('filial')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', id)
      .eq('ativo', true)
      .is('deleted_at', null);

    setUsageMetrics({
      usuarios: usuariosCount || 0,
      filiais: filiaisCount || 0
    });

    // 5. Logo
    await loadLogo(id);

    return tenantRecord;
  }, [formatCNPJ, formatCEP, loadLogo]);

  // Handler para salvar registro (usado pelo GenericDrawer)
  const handleSaveRecord = useCallback(async (formData: FormData) => {
    const auditFields = getAuditFields();

    // Merge config existente com usa_bvs atualizado
    const updatedConfig = {
      ...tenantConfig,
      usa_bvs: formData.usa_bvs || false
    };

    const dataToSave = {
      nome: formData.nome?.trim(),
      nome_fantasia: formData.nome_fantasia?.trim() || null,
      cnpj: formData.cnpj?.replace(/\D/g, '') || null,
      // Endereço
      cep: formData.cep?.replace(/\D/g, '') || null,
      logradouro: formData.logradouro?.trim() || null,
      numero: formData.numero?.trim() || null,
      complemento: formData.complemento?.trim() || null,
      bairro: formData.bairro?.trim() || null,
      localizacao: formData.localizacao?.trim() || null,
      cidade_id: formData.cidade_id || null,
      // Config
      config: updatedConfig,
      // Recebimento de obras entregues
      responsavel_obra_entrega_pessoa_id: formData.responsavel_obra_entrega_pessoa_id || null,
      // Audit
      updated_by: auditFields.updated_by
    };

    // Validação: Razão Social obrigatória
    if (!dataToSave.nome) {
      showError({ title: 'Campo obrigatório', description: 'Razão Social é obrigatória' });
      throw new Error('Razão Social é obrigatória');
    }

    // Validação: CNPJ válido
    if (dataToSave.cnpj && !validateCNPJ(dataToSave.cnpj)) {
      setCnpjError('CNPJ inválido');
      showError({ title: 'Erro de validação', description: 'CNPJ inválido. Verifique os dígitos informados.' });
      throw new Error('CNPJ inválido');
    }

    const { error } = await supabase
      .from('tenant')
      .update(dataToSave)
      .eq('id', tenantId!);

    if (error) throw error;

    showSuccess({ title: 'Sucesso', description: 'Dados da empresa atualizados' });

    if (onSave) onSave();

    return { id: tenantId! };
  }, [tenantId, tenantConfig, getAuditFields, showSuccess, showError, onSave]);

  // Handler upload de logo
  const handleLogoUpload = useCallback(({ url }: { url: string }) => {
    setLogoUrl(url);
  }, []);

  const handleLogoRemove = useCallback(() => {
    setLogoUrl(null);
  }, []);

  // Limites efetivos (custom ou do plano)
  const limites = useMemo(() => {
    if (!contrato) return { usuarios: 0, filiais: 0 };

    return {
      usuarios: contrato.limite_usuarios_custom || contrato.plano?.limite_usuarios || 0,
      filiais: contrato.limite_filiais_custom || contrato.plano?.limite_filiais || 0
    };
  }, [contrato]);

  // Formatar cidade para exibição
  const formatCidade = useCallback((cidadeObj: Cidade | null | undefined): string => {
    if (!cidadeObj) return '-';
    return `${cidadeObj.cidade}${cidadeObj.estado?.uf ? ' / ' + cidadeObj.estado.uf : ''}`;
  }, []);

  // Calcular porcentagem de uso
  const calcPercentage = useCallback((used: number, limit: number): number => {
    if (!limit || limit === 0) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  }, []);

  // Cor da barra de progresso baseada no uso
  const getProgressColor = useCallback((percentage: number): string => {
    if (percentage >= 90) return '#dc2626'; // vermelho
    if (percentage >= 70) return '#f59e0b'; // amarelo/laranja
    return '#22c55e'; // verde
  }, []);

  // Renderizar barra de progresso
  const renderProgressBar = useCallback((used: number, limit: number, label: string, icon: React.ReactNode) => {
    const percentage = calcPercentage(used, limit);
    const color = getProgressColor(percentage);

    return (
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--muted)', fontSize: '12px' }}>
            {icon}
            {label}
          </div>
          <span style={{ fontSize: '12px', fontWeight: 500, color: DRAWER_STYLES.colors.text }}>
            {used} / {limit || '∞'}
          </span>
        </div>
        <div style={{
          height: '6px',
          background: DRAWER_STYLES.colors.border,
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${percentage}%`,
            background: color,
            borderRadius: '3px',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <div style={{ textAlign: 'right', fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
          {percentage}%
        </div>
      </div>
    );
  }, [calcPercentage, getProgressColor]);

  // Renderizar módulo
  const renderModulo = useCallback((moduloInfo: ModuloInfo, tenantModulo: TenantModulo | undefined) => {
    const isContratado = !!tenantModulo;
    const isTrial = tenantModulo?.is_trial;
    const isAtivo = tenantModulo?.ativo !== false;

    // Calcular dias restantes de trial
    let trialDaysLeft = null;
    if (isTrial && tenantModulo?.trial_fim) {
      const hoje = new Date();
      const fim = new Date(tenantModulo.trial_fim);
      const diff = Math.ceil((fim.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
      trialDaysLeft = diff > 0 ? diff : 0;
    }

    // Status visual
    let statusIcon, statusColor, statusBg, statusLabel;
    if (!isContratado) {
      statusIcon = <X size={14} />;
      statusColor = 'var(--muted)';
      statusBg = DRAWER_STYLES.colors.background;
      statusLabel = 'Não contratado';
    } else if (isTrial) {
      statusIcon = <Clock size={14} />;
      statusColor = '#b45309';
      statusBg = '#fef3c7';
      statusLabel = trialDaysLeft !== null ? `Trial (${trialDaysLeft} dias)` : 'Trial';
    } else if (isAtivo) {
      statusIcon = <Check size={14} />;
      statusColor = '#15803d';
      statusBg = '#dcfce7';
      statusLabel = 'Ativo';
    } else {
      statusIcon = <X size={14} />;
      statusColor = 'var(--danger)';
      statusBg = '#fee2e2';
      statusLabel = 'Inativo';
    }

    return (
      <div
        key={moduloInfo.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          background: isContratado ? 'white' : DRAWER_STYLES.colors.background,
          border: `1px solid ${isContratado ? DRAWER_STYLES.colors.border : 'transparent'}`,
          borderRadius: '6px',
          opacity: isContratado ? 1 : 0.6
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{
            color: statusColor,
            display: 'flex',
            alignItems: 'center'
          }}>
            {statusIcon}
          </span>
          <span style={{
            fontSize: '13px',
            fontWeight: isContratado ? 500 : 400,
            color: isContratado ? DRAWER_STYLES.colors.text : 'var(--muted)'
          }}>
            {moduloInfo.modulo}
          </span>
        </div>
        <span style={{
          fontSize: '11px',
          padding: '2px 8px',
          borderRadius: '10px',
          background: statusBg,
          color: statusColor,
          fontWeight: 500
        }}>
          {statusLabel}
        </span>
      </div>
    );
  }, []);

  // Handler customizado para CEP blur - usado dentro do renderContent
  const handleCepBlur = useCallback(async (formData: FormData, onChange: (field: string, value: unknown) => void) => {
    const cep = formData?.cep;
    const cleaned = cep?.replace(/\D/g, '');

    // Só busca se tem 8 dígitos
    if (!cleaned || cleaned.length !== 8) return;

    setLoadingCep(true);
    try {
      const address = await fetchAddressByCEP(cleaned);

      if (address) {
        // Atualizar campos de endereço
        onChange('logradouro', address.logradouro || formData.logradouro);
        onChange('bairro', address.bairro || formData.bairro);
        onChange('complemento', address.complemento || formData.complemento);

        // Buscar cidade correspondente pelo nome e UF
        if (address.cidade && address.uf) {
          const cidadeEncontrada = cidades.find(
            c => c.cidade?.toLowerCase() === address.cidade.toLowerCase() &&
                 c.estado?.uf?.toLowerCase() === address.uf.toLowerCase()
          );

          if (cidadeEncontrada) {
            onChange('cidade_id', cidadeEncontrada.id);
            onChange('cidade_obj', cidadeEncontrada);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setLoadingCep(false);
    }
  }, [cidades]);

  // Renderizar conteúdo customizado
  const renderContent = useCallback(({ isEditing, formData, onChange }: { isEditing: boolean; formData: FormData; onChange: (field: string, value: unknown) => void }) => {
    return (
      <>
        {/* U18 — tab strip: Dados básicos vs. Brand para assinatura */}
        <div style={{
          display: 'flex',
          gap: '4px',
          padding: '0 0 0 4px',
          borderBottom: '1px solid #e5e7eb',
          marginBottom: '16px',
          flexShrink: 0,
        }}>
          {(['dados', 'brand'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '10px 12px',
                fontSize: '13px',
                fontWeight: 600,
                color: activeTab === tab ? '#154680' : '#6b7280',
                borderBottom: `2px solid ${activeTab === tab ? '#154680' : 'transparent'}`,
              }}
            >
              {tab === 'dados' ? 'Dados' : 'Brand para assinatura'}
            </button>
          ))}
        </div>

        {activeTab === 'brand' ? (
          <BrandSignatureSetup
            isEditing={isEditing}
            tenantId={tenantId || ''}
            formData={formData as { nome?: string | null; nome_fantasia?: string | null; logo_url?: string | null }}
            tenantConfig={tenantConfig}
            setTenantConfig={setTenantConfig}
          />
        ) : (
          <>
        {/* Seção: Logo + Dados */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
          {/* Logo */}
          <FieldsetCard
            title="Logo"
            style={{ width: '140px', flexShrink: 0, marginBottom: 0 }}
          >
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Avatar
                imageUrl={logoUrl || ''}
                name={formData?.nome_fantasia || formData?.nome || ''}
                size={100}
                editable={isEditing}
                entidade="tenant"
                entidadeId={tenantId || ''}
                bucket="documento"
                folder="tenant_logo"
                papel="logo"
                onUpload={handleLogoUpload}
                onRemove={handleLogoRemove}
                isLoading={false}
              />
            </div>
            {!tenantId && isEditing && (
              <p style={{ margin: '8px 0 0', fontSize: '11px', color: 'var(--muted)', textAlign: 'center' }}>
                Salve para adicionar logo
              </p>
            )}
          </FieldsetCard>

          {/* Dados da Empresa */}
          <FieldsetCard
            title="Dados da Empresa"
            style={{ flex: 1, marginBottom: 0 }}
          >
            {/* Razão Social */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ ...DRAWER_STYLES.typography.label }}>Razão Social *</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData?.nome || ''}
                  onChange={(e) => onChange('nome', e.target.value)}
                  style={{ ...DRAWER_COMPONENTS.input }}
                  placeholder="Razão Social da empresa"
                />
              ) : (
                <div style={{ ...DRAWER_STYLES.typography.value }}>{formData?.nome || '-'}</div>
              )}
            </div>

            {/* Nome Fantasia */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ ...DRAWER_STYLES.typography.label }}>Nome Fantasia</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData?.nome_fantasia || ''}
                  onChange={(e) => onChange('nome_fantasia', e.target.value)}
                  style={{ ...DRAWER_COMPONENTS.input }}
                  placeholder="Nome Fantasia"
                />
              ) : (
                <div style={{ ...DRAWER_STYLES.typography.value }}>{formData?.nome_fantasia || '-'}</div>
              )}
            </div>

            {/* CNPJ */}
            <div>
              <label style={{ ...DRAWER_STYLES.typography.label }}>CNPJ</label>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={formData?.cnpj || ''}
                    onChange={(e) => {
                      onChange('cnpj', formatCNPJ(e.target.value));
                      setCnpjError(null);
                    }}
                    onBlur={() => {
                      // Validar CNPJ ao sair do campo
                      if (formData?.cnpj && !validateCNPJ(formData.cnpj)) {
                        setCnpjError('CNPJ inválido');
                      } else {
                        setCnpjError(null);
                      }
                    }}
                    style={{
                      ...DRAWER_COMPONENTS.input,
                      borderColor: cnpjError ? 'var(--danger)' : undefined,
                      background: cnpjError ? '#fef2f2' : undefined
                    }}
                    placeholder="00.000.000/0000-00"
                    maxLength={18}
                  />
                  {cnpjError && (
                    <span style={{
                      fontSize: '11px',
                      color: 'var(--danger)',
                      marginTop: '4px',
                      display: 'block'
                    }}>
                      {cnpjError}
                    </span>
                  )}
                </>
              ) : (
                <div style={{ ...DRAWER_STYLES.typography.value }}>{formData?.cnpj || '-'}</div>
              )}
            </div>
          </FieldsetCard>
        </div>

        {/* Seção: Endereço */}
        <FieldsetCard title="Endereço" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px', marginBottom: '12px' }}>
            {/* CEP */}
            <div>
              <label style={{ ...DRAWER_STYLES.typography.label }}>CEP</label>
              {isEditing ? (
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={formData?.cep || ''}
                    onChange={(e) => onChange('cep', formatCEP(e.target.value))}
                    onBlur={() => handleCepBlur(formData, onChange)}
                    style={{
                      ...DRAWER_COMPONENTS.input,
                      paddingRight: loadingCep ? '36px' : undefined
                    }}
                    placeholder="00000-000"
                    maxLength={9}
                    disabled={loadingCep}
                  />
                  {loadingCep && (
                    <Loader2
                      size={16}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        animation: 'spin 1s linear infinite',
                        color: 'var(--primary)'
                      }}
                    />
                  )}
                </div>
              ) : (
                <div style={{ ...DRAWER_STYLES.typography.value }}>{formData?.cep || '-'}</div>
              )}
            </div>

            {/* Logradouro */}
            <div>
              <label style={{ ...DRAWER_STYLES.typography.label }}>Logradouro</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData?.logradouro || ''}
                  onChange={(e) => onChange('logradouro', e.target.value)}
                  style={{ ...DRAWER_COMPONENTS.input }}
                  placeholder="Rua, Avenida..."
                />
              ) : (
                <div style={{ ...DRAWER_STYLES.typography.value }}>{formData?.logradouro || '-'}</div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            {/* Número */}
            <div>
              <label style={{ ...DRAWER_STYLES.typography.label }}>Número</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData?.numero || ''}
                  onChange={(e) => onChange('numero', e.target.value)}
                  style={{ ...DRAWER_COMPONENTS.input }}
                  placeholder="Nº"
                />
              ) : (
                <div style={{ ...DRAWER_STYLES.typography.value }}>{formData?.numero || '-'}</div>
              )}
            </div>

            {/* Complemento */}
            <div>
              <label style={{ ...DRAWER_STYLES.typography.label }}>Complemento</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData?.complemento || ''}
                  onChange={(e) => onChange('complemento', e.target.value)}
                  style={{ ...DRAWER_COMPONENTS.input }}
                  placeholder="Sala, Andar..."
                />
              ) : (
                <div style={{ ...DRAWER_STYLES.typography.value }}>{formData?.complemento || '-'}</div>
              )}
            </div>

            {/* Bairro */}
            <div>
              <label style={{ ...DRAWER_STYLES.typography.label }}>Bairro</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData?.bairro || ''}
                  onChange={(e) => onChange('bairro', e.target.value)}
                  style={{ ...DRAWER_COMPONENTS.input }}
                  placeholder="Bairro"
                />
              ) : (
                <div style={{ ...DRAWER_STYLES.typography.value }}>{formData?.bairro || '-'}</div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {/* Cidade */}
            <div>
              <label style={{ ...DRAWER_STYLES.typography.label }}>Cidade</label>
              {isEditing ? (
                <SearchableSelect
                  options={cidades.map(c => ({
                    value: c.id,
                    label: `${c.cidade}${c.estado?.uf ? ' / ' + c.estado.uf : ''}`
                  }))}
                  value={formData?.cidade_id ? {
                    value: formData.cidade_id,
                    label: formatCidade(formData.cidade_obj || cidades.find(c => c.id === formData.cidade_id))
                  } : null}
                  onChange={(opt: { value: string } | null) => onChange('cidade_id', opt?.value || null)}
                  placeholder="Buscar cidade..."
                />
              ) : (
                <div style={{ ...DRAWER_STYLES.typography.value }}>
                  {formatCidade(formData?.cidade_obj)}
                </div>
              )}
            </div>

            {/* Localização/Referência */}
            <div>
              <label style={{ ...DRAWER_STYLES.typography.label }}>Referência</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData?.localizacao || ''}
                  onChange={(e) => onChange('localizacao', e.target.value)}
                  style={{ ...DRAWER_COMPONENTS.input }}
                  placeholder="Ponto de referência"
                />
              ) : (
                <div style={{ ...DRAWER_STYLES.typography.value }}>{formData?.localizacao || '-'}</div>
              )}
            </div>
          </div>
        </FieldsetCard>

        {/* Seção: Configurações */}
        <FieldsetCard title="Configurações" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            {isEditing ? (
              <input
                type="checkbox"
                checked={formData?.usa_bvs || false}
                onChange={(e) => onChange('usa_bvs', e.target.checked)}
                style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer' }}
              />
            ) : (
              <div style={{
                width: '18px',
                height: '18px',
                marginTop: '2px',
                borderRadius: '4px',
                border: '1px solid var(--panel-border)',
                background: formData?.usa_bvs ? '#154680' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {formData?.usa_bvs && <Check size={12} style={{ color: 'white' }} />}
              </div>
            )}
            <div>
              <label style={{ ...DRAWER_STYLES.typography.label, marginBottom: '4px', display: 'block' }}>
                Usa método BVS
              </label>
              <p style={{ fontSize: '12px', color: 'var(--muted)', margin: 0 }}>
                Habilita escolha de cabeamento BVS no Planejamento Automático
              </p>
            </div>
          </div>
        </FieldsetCard>

        {/* Seção: Recebimento de Obras Entregues */}
        <FieldsetCard title="Recebimento de Obras Entregues" style={{ marginBottom: '16px' }}>
          <div>
            <label style={{ ...DRAWER_STYLES.typography.label, marginBottom: '4px', display: 'block' }}>
              Responsável por confirmar a data de entrega
            </label>
            <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '0 0 8px 0' }}>
              Pessoa que recebe notificação quando uma obra atinge 100% e precisa ter a data de entrega confirmada manualmente. Deixe em branco se ninguém deve receber notificações automáticas.
            </p>
            {isEditing ? (
              <SmartSelect
                entity="pessoa"
                value={formData?.responsavel_obra_entrega_pessoa_id || null}
                onChange={(opt: { value: string } | null) =>
                  onChange('responsavel_obra_entrega_pessoa_id', opt?.value || null)
                }
                allowClear
                clearLabel="(Ninguém)"
                placeholder="Selecione uma pessoa..."
              />
            ) : (
              <div style={{ ...DRAWER_STYLES.typography.value }}>
                {formData?.responsavel_obra_entrega_rel
                  ? formData.responsavel_obra_entrega_rel.apelido
                    || formData.responsavel_obra_entrega_rel.nome
                    || '-'
                  : <span style={{ color: 'var(--muted)' }}>Ninguém configurado</span>}
              </div>
            )}
          </div>
        </FieldsetCard>

        {/* Seção: Meu Plano (readonly) */}
        <FieldsetCard title="Meu Plano" style={{ marginBottom: '16px' }}>
          {contrato ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ fontSize: '16px', fontWeight: 600, color: DRAWER_STYLES.colors.text }}>
                  {contrato.plano?.nome || 'Plano não identificado'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                  {contrato.data_fim
                    ? `Válido até ${formatDate(contrato.data_fim)}`
                    : 'Sem data de expiração'}
                </div>
              </div>

              {/* Barras de uso */}
              {renderProgressBar(
                usageMetrics.usuarios,
                limites.usuarios,
                'Usuários',
                <Users size={14} />
              )}
              {renderProgressBar(
                usageMetrics.filiais,
                limites.filiais,
                'Filiais',
                <GitBranch size={14} />
              )}

              {/* Botão trocar plano (desabilitado - futuro) */}
              <button
                disabled
                style={{
                  width: '100%',
                  padding: '10px',
                  marginTop: '8px',
                  border: '1px dashed var(--panel-border)',
                  borderRadius: '6px',
                  background: 'transparent',
                  color: 'var(--muted)',
                  fontSize: '13px',
                  cursor: 'not-allowed'
                }}
                title="Funcionalidade em desenvolvimento"
              >
                Trocar Plano (em breve)
              </button>
            </>
          ) : (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)' }}>
              Nenhum contrato ativo encontrado
            </div>
          )}
        </FieldsetCard>

        {/* Seção: Módulos */}
        <FieldsetCard title={`Módulos (${modulos.filter(m => m.ativo !== false).length})`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {allModulos.map(moduloInfo => {
              const tenantModulo = modulos.find(m => m.modulo?.id === moduloInfo.id);
              return renderModulo(moduloInfo, tenantModulo);
            })}

            {allModulos.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)' }}>
                Nenhum módulo disponível
              </div>
            )}

            {/* Legenda */}
            <div style={{
              marginTop: '12px',
              padding: '10px',
              background: DRAWER_STYLES.colors.background,
              borderRadius: '6px',
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              fontSize: '11px',
              color: 'var(--muted)'
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Check size={12} style={{ color: '#15803d' }} /> Ativo
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Clock size={12} style={{ color: '#b45309' }} /> Trial
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <X size={12} /> Não contratado
              </span>
            </div>
          </div>
        </FieldsetCard>

        {/* CSS Animation */}
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
          </>
        )}
      </>
    );
  }, [
    tenantId, logoUrl, cidades, contrato, modulos, allModulos, limites, usageMetrics,
    cnpjError, loadingCep, formatCNPJ, formatCEP, formatCidade,
    handleLogoUpload, handleLogoRemove, handleCepBlur, renderProgressBar, renderModulo,
    activeTab, tenantConfig,
  ]);

  // Não renderizar se não tem tenant
  if (!tenantId) return null;

  return (
    <GenericDrawer
      isOpen={isOpen}
      onClose={onClose}
      recordId={tenantId}
      title="Minha Empresa"
      mode={mode}
      onModeChange={setMode}
      onLoadRecord={handleLoadRecord}
      onSaveRecord={handleSaveRecord}
      renderContent={renderContent}
      permissions={{ canEdit, canDelete: false }}
    />
  );
}
